import { Copy, Download, EllipsisVertical, Loader2, PenLine, Save } from "lucide-react"
import { createPortal } from "react-dom"
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { cn } from "@/lib/utils"

const MORE_BTN_RESERVE_PX = 36
const ROW_GAP_PX = 6
const RESOLUTION_RESERVE_PX = 88
const SM_UP_MQ = "(min-width: 640px)"

const ACTION_BTN_CLASS =
  "inline-flex items-center gap-1.5 rounded-lg border border-ui-border bg-ui-surface-raised px-2.5 py-1.5 text-xs text-ui-text-primary transition-colors hover:bg-ui-surface-raised disabled:opacity-40"

const ICON_BTN_CLASS = cn(ACTION_BTN_CLASS, "px-2")

type ViewAction = {
  key: string
  label: string
  title?: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
}

type FitResult = {
  iconOnly: boolean
  visibleCount: number
  showResolution: boolean
}

export type SketchViewHeaderActionsProps = {
  /** Макс. ширина блока действий (ширина диалога − title − close − отступы). */
  maxWidth: number
  resolution: { w: number; h: number } | null
  copying: boolean
  showEdit: boolean
  showDownload?: boolean
  /** Image copy; hide for video view. Default true. */
  showCopy?: boolean
  /** Previz trim: only while the clip window is shorter than the file. */
  showSave?: boolean
  saveBusy?: boolean
  onDownload: () => void
  onCopy: () => void
  onEdit: () => void
  onSave?: () => void
  labels: {
    download: string
    copy: string
    copyTitle: string
    edit: string
    save: string
    menuAria: string
  }
}

function fitCount(widths: number[], available: number, reserveMore: number): number {
  let used = 0
  let count = 0
  for (const width of widths) {
    const next = used + (count > 0 ? ROW_GAP_PX : 0) + width
    if (next > available - reserveMore + 0.5) break
    used = next
    count += 1
  }
  return count
}

function allFit(widths: number[], available: number): boolean {
  return widths.length === 0 || fitCount(widths, available, 0) === widths.length
}

function actionsAvailable(available: number, resolutionW: number, withResolution: boolean): number {
  if (!withResolution || resolutionW <= 0) return available
  return Math.max(0, available - resolutionW - ROW_GAP_PX)
}

function computeFit(labeledWidths: number[], iconWidths: number[], available: number, resolutionW: number): FitResult {
  const itemCount = labeledWidths.length
  const hasResolution = resolutionW > 0

  if (allFit(labeledWidths, actionsAvailable(available, resolutionW, hasResolution))) {
    return { iconOnly: false, visibleCount: itemCount, showResolution: hasResolution }
  }
  if (hasResolution && allFit(labeledWidths, available)) {
    return { iconOnly: false, visibleCount: itemCount, showResolution: false }
  }
  if (allFit(iconWidths, actionsAvailable(available, resolutionW, hasResolution))) {
    return { iconOnly: true, visibleCount: itemCount, showResolution: hasResolution }
  }
  if (hasResolution && allFit(iconWidths, available)) {
    return { iconOnly: true, visibleCount: itemCount, showResolution: false }
  }

  const visibleCount = fitCount(iconWidths, available, MORE_BTN_RESERVE_PX)
  return { iconOnly: true, visibleCount, showResolution: false }
}

function ActionButton({ action, iconOnly }: { action: ViewAction; iconOnly: boolean }) {
  const hint = action.title ?? action.label
  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      className={iconOnly ? ICON_BTN_CLASS : ACTION_BTN_CLASS}
      title={hint}
      aria-label={iconOnly ? hint : undefined}
    >
      {action.icon}
      {iconOnly ? null : action.label}
    </button>
  )
}

export function SketchViewHeaderActions({
  maxWidth,
  resolution,
  copying,
  showEdit,
  showDownload = true,
  showCopy = true,
  showSave = false,
  saveBusy = false,
  onDownload,
  onCopy,
  onEdit,
  onSave,
  labels,
}: SketchViewHeaderActionsProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(3)
  const [iconOnly, setIconOnly] = useState(false)
  const [showResolution, setShowResolution] = useState(true)
  const [menuOpen, setMenuOpen] = useState(false)
  const [smUp, setSmUp] = useState(() => (typeof window === "undefined" ? true : window.matchMedia(SM_UP_MQ).matches))

  useEffect(() => {
    const mq = window.matchMedia(SM_UP_MQ)
    const apply = () => setSmUp(mq.matches)
    apply()
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [])

  const actions = useMemo<ViewAction[]>(() => {
    const items: ViewAction[] = []
    if (showSave) {
      items.push({
        key: "save",
        label: labels.save,
        title: labels.save,
        icon: saveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />,
        onClick: () => onSave?.(),
        disabled: saveBusy,
      })
    }
    if (showDownload) {
      items.push({
        key: "download",
        label: labels.download,
        title: labels.download,
        icon: <Download className="h-4 w-4" />,
        onClick: onDownload,
      })
    }
    if (showCopy) {
      items.push({
        key: "copy",
        label: labels.copy,
        title: labels.copyTitle,
        icon: copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />,
        onClick: onCopy,
        disabled: copying,
      })
    }
    if (showEdit) {
      items.push({
        key: "edit",
        label: labels.edit,
        icon: <PenLine className="h-4 w-4" />,
        onClick: onEdit,
      })
    }
    return items
  }, [copying, labels, onCopy, onDownload, onEdit, onSave, saveBusy, showCopy, showDownload, showEdit, showSave])

  useEffect(() => {
    const measure = measureRef.current
    if (!measure || typeof ResizeObserver === "undefined") return

    const recompute = () => {
      const labeled = Array.from(measure.querySelectorAll("[data-action-measure='labeled']")) as HTMLElement[]
      const icons = Array.from(measure.querySelectorAll("[data-action-measure='icon']")) as HTMLElement[]
      const labeledWidths = labeled.map((chip) => chip.offsetWidth)
      const iconWidths = icons.map((chip) => chip.offsetWidth)
      const available = Math.max(0, maxWidth)

      let resolutionW = 0
      if (smUp && resolution) {
        const resChip = measure.querySelector("[data-resolution-measure]") as HTMLElement | null
        resolutionW = resChip?.offsetWidth ?? RESOLUTION_RESERVE_PX
      }

      const fit = computeFit(labeledWidths, iconWidths, available, resolutionW)
      setIconOnly(fit.iconOnly)
      setVisibleCount(fit.visibleCount)
      setShowResolution(smUp && fit.showResolution)
    }

    recompute()
    const observer = new ResizeObserver(recompute)
    observer.observe(measure)
    return () => observer.disconnect()
  }, [actions, maxWidth, resolution, smUp])

  const inlineActions = actions.slice(0, visibleCount)
  const overflowActions = actions.slice(visibleCount)

  useEffect(() => {
    if (overflowActions.length === 0 && menuOpen) setMenuOpen(false)
  }, [menuOpen, overflowActions.length])

  const overflowKey = overflowActions.map((a) => a.key).join(",")
  const { menuStyle, portalRoot } = useDropdownMenu({
    open: menuOpen,
    direction: "down",
    align: "right",
    anchorRef: moreButtonRef,
    menuRef,
    rootRef,
    positionKey: overflowKey,
    estimatedMenuWidthPx: 192,
    resolveWidth: () => ({
      minWidth: "12rem",
      width: "max-content",
      maxWidth: "min(24rem, calc(100vw - 16px))",
    }),
    onOutsideClick: () => setMenuOpen(false),
  })

  return (
    <div ref={rootRef} className="flex max-w-full shrink-0 items-center gap-1.5" style={{ maxWidth }}>
      <div
        ref={measureRef}
        className="pointer-events-none fixed top-0 left-0 -z-50 flex items-center gap-1.5 opacity-0"
        aria-hidden
      >
        {resolution ? (
          <span data-resolution-measure className="text-xs text-ui-text-secondary tabular-nums">
            {resolution.w}&times;{resolution.h}
          </span>
        ) : null}
        {actions.map((action) => (
          <span key={`${action.key}-labeled`} data-action-measure="labeled" className={ACTION_BTN_CLASS}>
            {action.icon}
            {action.label}
          </span>
        ))}
        {actions.map((action) => (
          <span key={`${action.key}-icon`} data-action-measure="icon" className={ICON_BTN_CLASS}>
            {action.icon}
          </span>
        ))}
      </div>

      {showResolution && resolution ? (
        <span className="shrink-0 text-xs text-ui-text-secondary tabular-nums">
          {resolution.w}&times;{resolution.h}
        </span>
      ) : null}

      {inlineActions.map((action) => (
        <ActionButton key={action.key} action={action} iconOnly={iconOnly} />
      ))}

      {overflowActions.length > 0 ? (
        <div className="relative shrink-0">
          <button
            ref={moreButtonRef}
            type="button"
            className={ICON_BTN_CLASS}
            title={labels.menuAria}
            aria-label={labels.menuAria}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <EllipsisVertical className="h-4 w-4" />
          </button>
          {menuOpen && portalRoot
            ? createPortal(
                <div
                  ref={menuRef}
                  role="menu"
                  style={menuStyle ?? undefined}
                  className="overflow-hidden rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40"
                >
                  {overflowActions.map((action) => (
                    <button
                      key={action.key}
                      type="button"
                      role="menuitem"
                      title={action.title}
                      disabled={action.disabled}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-ui-text-primary hover:bg-ui-surface-sunken disabled:pointer-events-none disabled:opacity-50"
                      onClick={() => {
                        setMenuOpen(false)
                        action.onClick()
                      }}
                    >
                      {action.icon}
                      {action.label}
                    </button>
                  ))}
                </div>,
                portalRoot,
              )
            : null}
        </div>
      ) : null}
    </div>
  )
}
